/**
 * Delete the cookies a category already set, when that category is turned off.
 *
 * Stopping the script from loading again is not the whole of withdrawal: the
 * cookies written while consent was given are still on the device, still
 * identifying the same person. The privacy policy promises withdrawal at any
 * time, so a no has to remove what a yes created.
 *
 * Only first-party cookies written by JavaScript can be removed this way, which
 * is exactly what these trackers use (PostHog's `ph_…`, Meta's `_fbp`/`_fbc`,
 * Google's `_ga`/`_gid`/`_gcl…`). Anything set with HttpOnly by a server is out
 * of reach from here and is not something these trackers do.
 */
import type { ConsentCategory } from "./types";

/** Cookie-name patterns each category is responsible for. */
const OWNED_BY: Partial<Record<ConsentCategory, RegExp[]>> = {
  analytics: [/^ph_.*_posthog$/, /^_ga($|_)/, /^_gid$/],
  marketing: [/^_fb[a-z]?$/, /^_gcl_/, /^_uet/],
};

/**
 * Every domain a tracker on this host might have written the cookie on.
 *
 * The host itself is not enough. Google Analytics and Meta both set on the
 * registrable domain so the cookie is shared across subdomains — on
 * `pro.earlyup.ai` that is `.earlyup.ai`, which a host-only delete never
 * touches. So walk up the labels.
 *
 * Stops before the last two labels: a cookie can never be set on a public
 * suffix like `.ai`, so trying is noise, and on `localhost` (one label) the
 * walk yields just the host.
 */
export function domainCandidates(hostname: string): string[] {
  const out = new Set<string>(["", hostname, `.${hostname}`]);
  const labels = hostname.split(".");
  for (let i = 1; i <= labels.length - 2; i += 1) {
    const parent = labels.slice(i).join(".");
    out.add(parent);
    out.add(`.${parent}`);
  }
  return [...out];
}

function expire(name: string): void {
  const { hostname, pathname } = window.location;
  // A cookie is only deleted by a matching name AND path AND domain, and these
  // are written on several shapes — one wrong guess looks exactly like a cookie
  // that refuses to die. Cover the shapes rather than guess.
  const paths = new Set([
    "/",
    pathname,
    pathname.replace(/\/[^/]*$/, "") || "/",
  ]);
  const domains = domainCandidates(hostname);
  for (const path of paths) {
    for (const domain of domains) {
      document.cookie =
        `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}` +
        (domain ? `; domain=${domain}` : "");
    }
  }
}

/**
 * Remove every cookie belonging to the categories the visitor has just refused.
 * Returns the names it tried to remove, so a caller can log or a test can assert.
 */
export function clearCookiesFor(refused: ConsentCategory[]): string[] {
  if (typeof document === "undefined" || !document.cookie) return [];
  const patterns = refused.flatMap((category) => OWNED_BY[category] ?? []);
  if (patterns.length === 0) return [];

  const present = document.cookie
    .split(";")
    .map((entry) => entry.trim().split("=")[0])
    .filter((name): name is string => Boolean(name));

  const removed: string[] = [];
  for (const name of present) {
    if (patterns.some((pattern) => pattern.test(name))) {
      expire(name);
      removed.push(name);
    }
  }
  return removed;
}
